/* eslint-disable quote-props */
import { decodeXml } from '../utils/encodeXML.ts';
import type { FilterCategory, FilterCategoryName, SearchFilterVO } from '../search/types/searchFilter';
import { objectKeysUnsafe } from '../utils/typescript/objects';

const filterAliases: Record<string, string> = {
    'applied learning': 'life skills',
    'applied sciences': 'engineering & technology',
    'art': 'visual arts',
    'civics': 'civics & government',
    'eighth grade': '8th grade',
    'eleventh grade': '11th grade',
    'environmental science': 'climate & the environment',
    'equity focus*': 'historically underfunded schools due to economic and racial inequity',
    'esl': 'english language learning',
    'fifth grade': '5th grade',
    'first grade': '1st grade',
    'foreign languages': 'world languages',
    'fourth grade': '4th grade',
    'health': 'health education',
    'high school': 'grades 9-12',
    'math': 'mathematics',
    'maths': 'mathematics',
    'middle school': 'grades 6-8',
    'ninth grade': '9th grade',
    'nursery school': 'pre-k',
    'pe': 'gym & fitness',
    'physical education*': 'gym & fitness',
    'pre-kindergarten': 'pre-k',
    'prekindergarten': 'pre-k',
    'preschool': 'pre-k',
    'racial justice': 'racial justice & representation',
    'second grade': '2nd grade',
    'sel': 'social emotional learning',
    'seventh grade': '7th grade',
    'sixth grade': '6th grade',
    'social emotional': 'social emotional learning',
    'special education': 'students with disabilities',
    'special needs': 'students with disabilities',
    'sports': 'team sports',
    'tenth grade': '10th grade',
    'theater': 'performing arts',
    'theatre': 'performing arts',
    'third grade': '3rd grade',
    'twelfth grade': '12th grade',
};

function escapeFilterName(name: string) {
    return decodeXml(name.toLowerCase()).replace(/[^a-z0-9 -$]+/g, '').replace(/ needed$/, '');
}

function searchFilterList(keywords: string, filters: SearchFilterVO[]): SearchFilterVO | undefined {
    const escapedKeywords = escapeFilterName(keywords);
    const isMatchingFilter = (text: string) => escapedKeywords === escapeFilterName(text);

    for (let i = 0; i < filters.length; i++) {
        let filter: SearchFilterVO | undefined = filters[i];
        const aliases = filter.keywordFilterAliases;

        // eslint-disable-next-line no-mixed-operators
        if (isMatchingFilter(filter.text) || aliases && aliases.some(isMatchingFilter)) {
            return filter;
        }

        if (filter.children) {
            filter = searchFilterList(keywords, filter.children);
            if (filter) return filter;
        }
    }
}

/**
 * * This function is used to convert free keyword searches to structured filters,
 * which can improve search results.
 * @param keywords The keywords to convert to a filter
 * @param filterCategories The filters to look through
 * @returns A structured filter that matches the keyword search, if it exists
 */
export function convertKeywordsToFilter(keywords: string, filterCategories: FilterCategory[]) {
    keywords = keywords.toLowerCase();
    // Suppressing an error telling us to use Object.hasOwn(object, property) instead of
    // object.hasOwnProperty(property) here.
    //
    // Object.hasOwn only came out in 2021; a decent chunk of browsers don't support it yet (2022-04-29).
    // I don't think we're overwriting hasOwnProperty (which is what Object.hasOwn guards against) anyway.
    //
    // eslint-disable-next-line no-prototype-builtins
    if (filterAliases.hasOwnProperty(keywords)) {
        keywords = filterAliases[keywords];
    }

    for (let i = 0; i < filterCategories.length; i++) {
        const section = filterCategories[i];
        const sectionName: FilterCategoryName = objectKeysUnsafe(section)[0];
        const filters = section[sectionName];
        const filter = searchFilterList(keywords, filters);
        if (filter) return filter;
    }
}
